import { useState, useMemo, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Palette, Upload, History, Search, X, Calendar, MessageSquare,
  Image, CheckCircle, Clock, User, Eye,
  RefreshCw, AlertCircle, Package
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getMexicoDateString } from '@/lib/mexicoDate';

interface ProductDesign {
  id: number;
  code: string;
  name: string;
  latestDesignPath: string | null;
  latestDesign: {
    id: number;
    version: number;
    uploadedAt: string;
    imageDate: string | null;
    comments: string | null;
    uploaderName: string | null;
    uploaderUsername: string | null;
  } | null;
}

interface DesignVersion {
  id: number;
  version: number;
  storagePath: string;
  imageDate: string | null;
  comments: string | null;
  uploadedAt: string;
  uploaderName: string | null;
  uploaderUsername: string | null;
}

const token = () => localStorage.getItem('access_token') || '';

const authHeader = () => ({ Authorization: `Bearer ${token()}` });

async function apiFetch(url: string, opts?: RequestInit) {
  const res = await fetch(url, {
    ...opts,
    headers: { ...authHeader(), ...(opts?.headers || {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Error ${res.status}`);
  }
  return res.json();
}

function formatDate(dateStr: string | null | undefined) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function DesignModule() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<ProductDesign | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState<{ productId: number; versionId?: number; version?: number } | null>(null);

  // Upload form state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadDate, setUploadDate] = useState('');
  const [uploadComments, setUploadComments] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load all PRODUCTO_TERMINADO products
  const { data: products = [], isLoading, refetch } = useQuery<ProductDesign[]>({
    queryKey: ['design-products'],
    queryFn: () => apiFetch('/api/catalog-designs/products'),
    staleTime: 30_000,
  });

  // Load versions for selected product (when versions modal is open)
  const { data: versions = [], isLoading: versionsLoading } = useQuery<DesignVersion[]>({
    queryKey: ['design-versions', selectedProduct?.id],
    queryFn: () => apiFetch(`/api/catalog-designs/products/${selectedProduct!.id}/versions`),
    enabled: showVersionsModal && !!selectedProduct,
  });

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async ({ productId, file, date, comments }: { productId: number; file: File; date: string; comments: string }) => {
      const formData = new FormData();
      formData.append('image', file);
      if (date) formData.append('imageDate', date);
      if (comments) formData.append('comments', comments);

      const res = await fetch(`/api/catalog-designs/products/${productId}/upload`, {
        method: 'POST',
        headers: authHeader(),
        body: formData,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Error al subir');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['design-products'] });
      queryClient.invalidateQueries({ queryKey: ['design-versions', selectedProduct?.id] });
      closeUploadModal();
      alert('Diseño subido exitosamente');
    },
    onError: (err: Error) => {
      alert(err.message);
    },
  });

  // Filtered products based on search (client-side, all products)
  const filteredProducts = useMemo(() => {
    if (!searchTerm.trim()) return products;
    const term = searchTerm.toLowerCase();
    return products.filter(p =>
      p.name.toLowerCase().includes(term) ||
      p.code.toLowerCase().includes(term)
    );
  }, [products, searchTerm]);

  const handleFileSelect = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) {
      alert('Solo se permiten archivos de imagen');
      return;
    }
    setUploadFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setPreviewUrl(e.target?.result as string);
    reader.readAsDataURL(file);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, [handleFileSelect]);

  const todayISO = () => getMexicoDateString();

  const openUploadModal = (product: ProductDesign) => {
    setSelectedProduct(product);
    setUploadFile(null);
    setPreviewUrl(null);
    setUploadDate(todayISO());
    setUploadComments('');
    setShowUploadModal(true);
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setUploadFile(null);
    setPreviewUrl(null);
  };

  const openVersionsModal = (product: ProductDesign) => {
    setSelectedProduct(product);
    setShowVersionsModal(true);
  };

  const handleSubmitUpload = () => {
    if (!uploadFile || !selectedProduct) return;
    uploadMutation.mutate({
      productId: selectedProduct.id,
      file: uploadFile,
      date: uploadDate,
      comments: uploadComments,
    });
  };

  const imageUrl = (productId: number, version?: number | null) =>
    `/api/catalog-designs/products/${productId}/image${version != null ? `?v=${version}` : ''}`;

  const versionImageUrl = (versionId: number) =>
    `/api/catalog-designs/versions/${versionId}/image`;

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-purple-100 p-2 rounded-lg">
              <Palette className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Gestión de Diseños</h1>
              <p className="text-sm text-gray-500">Administra las imágenes de diseño de productos terminados</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
              {filteredProducts.length} de {products.length} productos
            </span>
            <Button variant="outline" size="sm" onClick={() => refetch()} className="gap-1.5">
              <RefreshCw className="w-3.5 h-3.5" /> Actualizar
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="mt-4 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nombre o código de producto..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Product List */}
      <div className="flex-1 overflow-auto p-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">Cargando productos...</p>
            </div>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center text-gray-400">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-lg font-medium">
                {searchTerm ? 'Sin resultados para tu búsqueda' : 'No hay productos terminados registrados'}
              </p>
              <p className="text-sm mt-1">
                {searchTerm ? 'Intenta con otro término' : 'Los productos se sincronizan desde el sistema externo'}
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Código</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Nombre del Producto</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-28">Diseño Actual</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Versión</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Última Actualización</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Subido por</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredProducts.map(product => (
                  <tr key={product.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                        {product.code}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-gray-900">{product.name}</span>
                    </td>
                    <td className="px-4 py-3">
                      {product.latestDesignPath ? (
                        <button
                          onClick={() => setShowImagePreview({ productId: product.id, version: product.latestDesign?.version ?? undefined })}
                          className="group relative w-14 h-14 rounded-lg overflow-hidden border-2 border-purple-200 hover:border-purple-400 transition-all"
                        >
                          <img
                            src={imageUrl(product.id, product.latestDesign?.version)}
                            alt={`Diseño ${product.name}`}
                            className="w-full h-full object-cover"
                            onError={e => { e.currentTarget.style.display = 'none'; }}
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                            <Eye className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </button>
                      ) : (
                        <div className="w-14 h-14 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                          <Image className="w-5 h-5 text-gray-300" />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {product.latestDesign ? (
                        <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                          <CheckCircle className="w-3 h-3" /> v{product.latestDesign.version}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Sin diseño</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {product.latestDesign ? (
                        <div className="flex items-center gap-1 text-xs text-gray-600">
                          <Clock className="w-3 h-3 text-gray-400" />
                          {formatDate(product.latestDesign.imageDate || product.latestDesign.uploadedAt)}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {product.latestDesign?.uploaderName || product.latestDesign?.uploaderUsername ? (
                        <div className="flex items-center gap-1 text-xs text-gray-600">
                          <User className="w-3 h-3 text-gray-400" />
                          {product.latestDesign.uploaderName || product.latestDesign.uploaderUsername}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          onClick={() => openUploadModal(product)}
                          className="gap-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs h-7 px-2.5"
                        >
                          <Upload className="w-3 h-3" /> Subir Diseño
                        </Button>
                        {product.latestDesign && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openVersionsModal(product)}
                            className="gap-1.5 text-xs h-7 px-2.5"
                          >
                            <History className="w-3 h-3" /> Historial
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b flex items-center justify-between bg-gradient-to-r from-purple-50 to-indigo-50 rounded-t-xl flex-shrink-0">
              <div>
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <Upload className="w-5 h-5 text-purple-600" /> Subir Diseño
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">{selectedProduct.name} ({selectedProduct.code})</p>
              </div>
              <button onClick={closeUploadModal} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Drag & Drop Area */}
              <div
                className={`relative border-2 border-dashed rounded-xl transition-all cursor-pointer
                  ${isDragOver ? 'border-purple-400 bg-purple-50' : 'border-gray-300 hover:border-purple-300 hover:bg-gray-50'}
                  ${previewUrl ? 'border-purple-400 bg-purple-50' : ''}`}
                onDrop={handleDrop}
                onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
                onDragLeave={() => setIsDragOver(false)}
                onClick={() => fileInputRef.current?.click()}
              >
                {previewUrl ? (
                  <div className="p-3">
                    <img
                      src={previewUrl}
                      alt="Vista previa"
                      className="w-full max-h-48 object-contain rounded-lg"
                    />
                    <p className="text-center text-xs text-purple-600 mt-2 font-medium">
                      {uploadFile?.name} · Haz clic para cambiar
                    </p>
                  </div>
                ) : (
                  <div className="p-10 text-center">
                    <div className="bg-purple-100 rounded-full w-14 h-14 flex items-center justify-center mx-auto mb-3">
                      <Upload className="w-7 h-7 text-purple-500" />
                    </div>
                    <p className="text-sm font-medium text-gray-700">Arrastra una imagen aquí</p>
                    <p className="text-xs text-gray-400 mt-1">o haz clic para seleccionar desde tu dispositivo</p>
                    <p className="text-xs text-gray-400 mt-1">PNG, JPG, WEBP · Máx. 10 MB</p>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) handleFileSelect(f);
                    e.target.value = '';
                  }}
                />
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <Calendar className="inline w-4 h-4 mr-1 text-gray-400" /> Fecha del diseño
                </label>
                <input
                  type="date"
                  value={uploadDate}
                  onChange={e => setUploadDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Comments */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <MessageSquare className="inline w-4 h-4 mr-1 text-gray-400" /> Comentarios
                  <span className="text-gray-400 font-normal ml-1">(opcional)</span>
                </label>
                <textarea
                  value={uploadComments}
                  onChange={e => setUploadComments(e.target.value)}
                  placeholder="Ej: Versión aprobada por el cliente, cambio en colores..."
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                />
              </div>

              {/* Info about versioning */}
              {selectedProduct.latestDesign && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700">
                    Este producto ya tiene un diseño (v{selectedProduct.latestDesign.version}). Al subir una nueva imagen se creará la versión <strong>v{selectedProduct.latestDesign.version + 1}</strong> y se convertirá en la oficial.
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t bg-gray-50 rounded-b-xl flex justify-end gap-3 flex-shrink-0">
              <Button variant="outline" onClick={closeUploadModal} disabled={uploadMutation.isPending}>
                Cancelar
              </Button>
              <Button
                onClick={handleSubmitUpload}
                disabled={!uploadFile || uploadMutation.isPending}
                className="bg-purple-600 hover:bg-purple-700 text-white gap-2"
              >
                {uploadMutation.isPending ? (
                  <><RefreshCw className="w-4 h-4 animate-spin" /> Subiendo...</>
                ) : (
                  <><Upload className="w-4 h-4" /> Subir Diseño</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Versions Modal */}
      {showVersionsModal && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="px-6 py-4 border-b flex items-center justify-between bg-gradient-to-r from-purple-50 to-indigo-50 rounded-t-xl flex-shrink-0">
              <div>
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <History className="w-5 h-5 text-purple-600" /> Historial de Diseños
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">{selectedProduct.name} ({selectedProduct.code})</p>
              </div>
              <button onClick={() => setShowVersionsModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {versionsLoading ? (
                <div className="flex items-center justify-center h-40">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
                </div>
              ) : versions.length === 0 ? (
                <div className="text-center text-gray-400 py-16">
                  <History className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p>Sin versiones registradas</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {versions.map((v, idx) => (
                    <div
                      key={v.id}
                      className={`rounded-xl border-2 transition-all overflow-hidden
                        ${idx === 0 ? 'border-purple-300' : 'border-gray-200 hover:border-gray-300'}`}
                    >
                      {/* Header row */}
                      <div className={`flex items-center justify-between px-4 py-2 ${idx === 0 ? 'bg-purple-600' : 'bg-gray-100'}`}>
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-bold ${idx === 0 ? 'text-white' : 'text-gray-700'}`}>
                            Versión {v.version}
                          </span>
                          {idx === 0 && (
                            <span className="text-[10px] bg-white text-purple-700 px-2 py-0.5 rounded-full font-bold">
                              OFICIAL ACTUAL
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => setShowImagePreview({ productId: selectedProduct.id, versionId: v.id })}
                          className={`text-xs flex items-center gap-1 px-2 py-1 rounded-lg transition-colors
                            ${idx === 0 ? 'bg-white/20 text-white hover:bg-white/30' : 'bg-white text-gray-600 hover:bg-gray-200'}`}
                        >
                          <Eye className="w-3 h-3" /> Ver imagen
                        </button>
                      </div>

                      {/* Body */}
                      <div className={`flex gap-4 p-4 ${idx === 0 ? 'bg-purple-50' : 'bg-white'}`}>
                        {/* Thumbnail */}
                        <button
                          onClick={() => setShowImagePreview({ productId: selectedProduct.id, versionId: v.id })}
                          className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden border border-gray-200 hover:border-purple-300 transition-colors group relative"
                        >
                          <img
                            src={versionImageUrl(v.id)}
                            alt={`v${v.version}`}
                            className="w-full h-full object-cover"
                            onError={e => { e.currentTarget.style.display = 'none'; }}
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center">
                            <Eye className="w-4 h-4 text-white opacity-0 group-hover:opacity-100" />
                          </div>
                        </button>

                        {/* Info grid */}
                        <div className="flex-1 min-w-0 space-y-2">
                          {/* Dates row */}
                          <div className="flex flex-wrap gap-4">
                            <div className="flex items-center gap-1.5">
                              <Calendar className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
                              <div>
                                <p className="text-[10px] text-gray-400 uppercase font-semibold tracking-wide">Fecha del diseño</p>
                                <p className={`text-sm font-semibold ${idx === 0 ? 'text-purple-700' : 'text-gray-700'}`}>
                                  {v.imageDate ? formatDate(v.imageDate) : <span className="text-gray-400 font-normal italic">No especificada</span>}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Clock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                              <div>
                                <p className="text-[10px] text-gray-400 uppercase font-semibold tracking-wide">Subido el</p>
                                <p className="text-sm font-semibold text-gray-700">{formatDate(v.uploadedAt)}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <User className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                              <div>
                                <p className="text-[10px] text-gray-400 uppercase font-semibold tracking-wide">Subido por</p>
                                <p className="text-sm font-semibold text-gray-700">
                                  {v.uploaderName || v.uploaderUsername || <span className="text-gray-400 font-normal italic">Desconocido</span>}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Comments */}
                          <div className={`rounded-lg px-3 py-2 ${v.comments ? (idx === 0 ? 'bg-purple-100' : 'bg-gray-50') : 'bg-gray-50 border border-dashed border-gray-200'}`}>
                            <div className="flex items-start gap-1.5">
                              <MessageSquare className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${v.comments ? (idx === 0 ? 'text-purple-500' : 'text-gray-400') : 'text-gray-300'}`} />
                              {v.comments ? (
                                <p className={`text-sm ${idx === 0 ? 'text-purple-800' : 'text-gray-700'}`}>{v.comments}</p>
                              ) : (
                                <p className="text-xs text-gray-400 italic">Sin comentarios</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="px-6 py-3 border-t bg-gray-50 rounded-b-xl flex justify-between items-center flex-shrink-0">
              <p className="text-xs text-gray-400">{versions.length} versión(es) registrada(s)</p>
              <Button variant="outline" size="sm" onClick={() => setShowVersionsModal(false)}>Cerrar</Button>
            </div>
          </div>
        </div>
      )}

      {/* Full-size Image Preview Modal */}
      {showImagePreview && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60] p-4"
          onClick={() => setShowImagePreview(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            onClick={() => setShowImagePreview(null)}
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={showImagePreview.versionId
              ? versionImageUrl(showImagePreview.versionId)
              : imageUrl(showImagePreview.productId, showImagePreview.version)
            }
            alt="Diseño"
            className="max-w-[95vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
